import * as React from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './UserProfileMenu.styles.scss';
import { Link } from 'react-router-dom';
import { userDisplayName } from '../../utils/user';
import useTranslation from '../../translations/use-translation';

const triggerBoxSx = { display: 'flex', alignItems: 'center', ml: 'auto' };
const triggerBtnSx = { ml: 2, p: 0.5 };

const menuPaperSx = {
  width: 272,
  mt: 1.5,
  overflow: 'hidden',
  padding: 0,
  // This menu is light-themed by design (dark text, teal header). Pin the
  // background to white so it stays readable even when the app is in dark mode
  // (otherwise it inherits the dark theme's #1e1e1e paper and text vanishes).
  backgroundColor: '#ffffff',
  color: '#1f2937',
  borderRadius: '14px !important',
  border: '1px solid rgba(36, 160, 171, 0.15)',
  boxShadow: '0 4px 8px rgba(0,0,0,0.04), 0 20px 48px rgba(11,74,80,0.16) !important',
  '& .MuiList-root': { padding: 0 },
  '& .MuiMenuItem-root': {
    padding: 0,
    minHeight: 'unset',
    '&:hover': { backgroundColor: 'transparent' },
  },
};

const UserProfileMenu = ({ displayName, userInfo, signOut }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const translate = useTranslation();
  const emailInitial = userInfo?.email?.[0]?.toUpperCase() ?? 'U';
  const profileImageLetters = userDisplayName(displayName) || emailInitial;
  const name = userInfo?.displayName?.split(' ').join('') ?? '';

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <React.Fragment>
      <Box sx={triggerBoxSx}>
        <Tooltip title={userInfo?.displayName ?? ''} placement="bottom">
          <IconButton
            onClick={handleClick}
            sx={triggerBtnSx}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label="Open account menu"
          >
            <Avatar className="avatar-bg profile-trigger-avatar">
              {profileImageLetters}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{ elevation: 0, sx: menuPaperSx }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* ── Profile header ── */}
        <div className="pm-header" tabIndex={-1} aria-hidden="true">
          <Avatar className="pm-header__avatar">
            {profileImageLetters}
          </Avatar>
          <div className="pm-header__info">
            <p className="pm-header__name">{userInfo?.displayName}</p>
            <p className="pm-header__email">{userInfo?.email}</p>
          </div>
        </div>

        {/* ── Section label ── */}
        <p className="pm-section-label">{translate('ACCOUNT')}</p>

        {/* ── Profile ── */}
        <MenuItem
          component={Link}
          to={`/${name}/profile`}
          onClick={handleClose}
          className="pm-item"
          disableRipple
        >
          <span className="pm-item__icon">
            <PersonOutlineIcon fontSize="small" />
          </span>
          <span className="pm-item__label">{translate('PROFILE')}</span>
          <ChevronRightIcon fontSize="small" className="pm-item__chevron" />
        </MenuItem>

        {/* ── Documents ── */}
        <MenuItem
          component={Link}
          to={`/${name}/documents`}
          onClick={handleClose}
          className="pm-item"
          disableRipple
        >
          <span className="pm-item__icon">
            <FileCopyOutlinedIcon fontSize="small" />
          </span>
          <span className="pm-item__label">{translate('DOCUMENTS')}</span>
          <ChevronRightIcon fontSize="small" className="pm-item__chevron" />
        </MenuItem>

        {/* ── Divider ── */}
        <div className="pm-divider" aria-hidden="true" />

        {/* ── Logout ── */}
        <MenuItem
          onClick={signOut}
          className="pm-item pm-item--logout"
          disableRipple
        >
          <span className="pm-item__icon pm-item__icon--danger">
            <PowerSettingsNewIcon fontSize="small" />
          </span>
          <span className="pm-item__label">{translate('LOGOUT')}</span>
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
};

export default UserProfileMenu;
